import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFile, faFilePdf, faFileWord, faFileExcel, faFileImage } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { createArchiveInstance, getFileDisplayName } from '../../Models/Archive';
import './Archive.css';

const getFileIcon = (fileUrl) => {
    const ext = (fileUrl?.split('.').pop()?.split('?')[0] || '').toLowerCase();
    if (['pdf'].includes(ext)) return faFilePdf;
    if (['doc', 'docx'].includes(ext)) return faFileWord;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return faFileExcel;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return faFileImage;
    return faFile;
};

const Archive = (props) => {
    const { t } = useTranslation();
    const [archive, setArchive] = useState(createArchiveInstance());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const inst = createArchiveInstance();
                await inst.load(props.id);
                inst.renderStyles();
                setArchive(inst);
                setIsLoading(false);
            } catch (err) {
                console.error('Failed to load archive:', err);
                setError('Failed to load archive');
                setIsLoading(false);
            }
        };
        fetchData();
    }, [props.id]);

    if (isLoading) {
        return <div className="loading-placeholder">Loading archive...</div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    const sectionId = archive.getId();
    const sectionTitle = archive.getSectionTitle();
    const files = archive.getFiles().filter((f) => f.fileUrl || f.title?.trim());

    return (
        <section className={`Archive Archive-${sectionId}`}>
            <Container className="Archive-inner">
                {sectionTitle?.trim() && (
                    <h2 className={`Archive-title ArchiveTitle-${sectionId}`}>
                        {sectionTitle}
                    </h2>
                )}
                {files.length === 0 ? (
                    <p className="Archive-empty">{t('Archive.NoFiles', 'No files available.')}</p>
                ) : (
                    <ul className="Archive-files list-unstyled mb-0">
                        {files.map((file) => {
                            const displayName = getFileDisplayName(file);
                            const canDownload = !!file.fileUrl;

                            return (
                                <li key={file.id}>
                                    <div className={`Archive-file-row ArchiveFile-${sectionId}`}>
                                        <div className="Archive-file-info">
                                            <div className="Archive-file-icon">
                                                <FontAwesomeIcon icon={getFileIcon(file.fileUrl)} />
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className={`Archive-file-title ArchiveFileTitle-${sectionId}`}>
                                                    {file.title?.trim() || displayName}
                                                </h3>
                                                {canDownload && file.title?.trim() && (
                                                    <div className="Archive-file-meta">{displayName}</div>
                                                )}
                                            </div>
                                        </div>
                                        {canDownload ? (
                                            <a
                                                href={file.fileUrl}
                                                className="Archive-download-btn"
                                                download={displayName}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <FontAwesomeIcon icon={faDownload} />
                                                {t('Archive.Download', 'Download')}
                                            </a>
                                        ) : (
                                            <span className="Archive-download-btn Archive-download-btn--disabled">
                                                <FontAwesomeIcon icon={faDownload} />
                                                {t('Archive.Download', 'Download')}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Container>
        </section>
    );
};

export default Archive;
